import { useEffect } from "react";
import { useMutation } from "@apollo/client";
import { Controller, useForm } from "react-hook-form";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import TextField from "@/components/TextField";
import TipCard from "@/components/TipCard";
import { UPDATE_PERSON_MUTATION } from "@/features/person/queries";
import { emailPattern } from "@/lib/emailHelpers";
import { useNavigate } from "@/router";
import EmailIdentityCheck from "../forms/EmailIdentityCheck";
import {
  EMAIL_IDENTITY_DIALOG_HEADLINE,
  EMAIL_IDENTITY_SAVE_HINT,
  type EmailIdentityCheckPerson
} from "../forms/emailIdentityCheck";
import { EditProps } from "../forms/types";

type FieldValues = {
  email: string;
};

interface EmailEditModalProps extends EditProps {
  open: boolean;
  email?: string;
  identityPerson: EmailIdentityCheckPerson;
}

const defaultErrorMessage = "Failed to update email";

export default function EmailEditModal({
  open,
  personId,
  email = "",
  identityPerson,
  onCancel,
  onSuccess,
  onError
}: EmailEditModalProps) {
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    setError,
    getValues,
    reset
  } = useForm({
    defaultValues: { email },
    mode: "onChange"
  });

  const navigate = useNavigate();
  const [updatePerson, { loading }] = useMutation(UPDATE_PERSON_MUTATION);
  const emailIsTaken = errors.email?.type === "409";

  useEffect(() => {
    if (open) {
      reset({ email });
    }
  }, [open, email, reset]);

  async function onSubmit({ email }: FieldValues) {
    try {
      const input = {
        id: personId,
        email
      };

      const { data } = await updatePerson({
        variables: { input }
      });
      const {
        updatePerson: { success, person, code, message }
      } = data;

      if (success) {
        onSuccess(person);
        onCancel();
        return;
      }
      if (code === 409) {
        return setError("email", { type: code.toString(), message: "Person already exists with this email" });
      }

      return onError(message || defaultErrorMessage);
    } catch (error) {
      return onError((error as Error).message || defaultErrorMessage);
    }
  }

  return (
    <Dialog
      open={open}
      onClosed={onCancel}
      headline={EMAIL_IDENTITY_DIALOG_HEADLINE}
      hasFormElements
      disableClose={loading}
      className="w-[560px] max-w-[90vw] max-h-[85vh]"
      actions={
        <>
          <Button variant="text" onClick={onCancel} testId="email-edit-cancel" disabled={loading}>
            Cancel
          </Button>
          <Button disabled={loading || !isValid} onClick={handleSubmit(onSubmit)} testId="email-edit-save">
            Save
          </Button>
        </>
      }
    >
      {open ? (
        <div className="flex flex-col gap-6" data-testid="email-edit-form">
          <EmailIdentityCheck person={identityPerson} />
          <Controller
            control={control}
            name="email"
            rules={{
              required: "Email is required",
              pattern: { value: emailPattern, message: "The email address is invalid" }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                label="Email"
                error={!!errors.email}
                errorText={errors.email?.message}
                supportingText={EMAIL_IDENTITY_SAVE_HINT}
              />
            )}
          />

          {emailIsTaken && (
            <TipCard
              onClick={() => navigate({ pathname: "/search", search: `q=${encodeURIComponent(getValues("email"))}` })}
            />
          )}
        </div>
      ) : null}
    </Dialog>
  );
}
